import React from 'react';
import { Checkbox } from '../../ui/Checkbox';
import { getShipmentStatusClasses } from '../../../utils/status';
import { CopyIcon } from 'lucide-react';
import toast from 'react-hot-toast';

export interface B2BOrderCardProps {
  order: any;
  isSelected?: boolean;
  selectable?: boolean;
  onSelectChange?: (checked: boolean) => void;
  onSelect?: (order: any) => void;
  onTrackingClick?: (order: any) => void;
}

export const B2BOrderCard: React.FC<B2BOrderCardProps> = ({
  order,
  isSelected = false,
  selectable = true,
  onSelectChange,
  onSelect,
  onTrackingClick,
}) => {
  const pickupName =
    order.senderName ||
    order.companyName ||
    order.owner ||
    'Sender';

  // Format exactly like B2C: Pincode, City, State
  const formatAddress = (pincode?: string, city?: string, state?: string, fallback?: string) => {
    const parts = [pincode, city, state].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : (fallback || 'N/A');
  };

  const pickupLocation = formatAddress(
    order.pickupPincode,
    order.senderCity,
    order.senderState,
    order.departure
  );

  const deliveryName =
    order.consigneeName ||
    order.customer ||
    'Customer';

  const deliveryLocation = formatAddress(
    order.deliveryPincode,
    order.consigneeCity,
    order.consigneeState,
    order.arrival
  );

  // Weight display (e.g. 13.65 Kg)
  const weightVal =
    order.chargeableWeight ||
    order.weight ||
    order.actualWeight ||
    0;
  const weightDisplay =
    typeof weightVal === 'string' && weightVal.toLowerCase().includes('kg')
      ? weightVal
      : `${weightVal} Kg`;

  // Amount display
  const amountVal = order.totalFreight ?? order.chargeableAmount ?? order.amount ?? 0;
  const numAmount = Number(amountVal);
  const hasAmount = !isNaN(numAmount) && numAmount > 0;

  // Date format strictly matching B2C: DD/MM/YYYY (e.g. 15/10/2026)
  const formatDateAsB2C = (val: any) => {
    if (!val) return '';
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val);
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '';
    }
  };

  const rawDate =
    order.createdAt ||
    order.rawOrder?.createdAt ||
    order.date?.timestamp ||
    order.date;
  const dateDisplay = formatDateAsB2C(rawDate) || 'N/A';

  // Tracking display
  const trackingNumber =
    order.trackingId ||
    order.awbNumber ||
    order.orderId ||
    (order.id ? `SP${order.id}` : 'No Tracking');

  return (
    <div
      onClick={() => onSelect?.(order)}
      className={`relative bg-white rounded-2xl shadow border pt-4 hover:shadow-md transition cursor-pointer overflow-hidden flex flex-col h-full ${
        isSelected ? 'border-2 border-teal bg-teal/5' : ''
      }`}
    >
      {/* Selection Checkbox */}
      {selectable && (
        <div
          className="absolute top-4 right-4 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onChange={(e) => onSelectChange?.(e.target.checked)}
          />
        </div>
      )}

      {/* Status & Route Visual */}
      <div className="flex flex-col gap-0 items-start justify-between">
        <div
          className={`ml-4 mb-2 w-fit h-fit text-[10px] px-2 py-0.5 border rounded-lg font-bold ${getShipmentStatusClasses(
            order.status
          )}`}
        >
          {order.status || 'Created'}
        </div>

        <div className="px-3 flex items-center gap-3 mb-3 w-full">
          <div className="flex flex-col items-center flex-shrink-0">
            <div className="w-2.5 h-2.5 bg-teal rounded-full" />
            <div className="w-0.5 h-10 border-l-2 border-dotted border-gray-300" />
            <div className="w-2.5 h-2.5 bg-gray-400 rounded-full" />
          </div>

          <div className="text-xs text-gray-700 min-w-0 flex-1">
            <div>
              <p className="font-bold text-[10px] text-teal truncate">
                Pickup ( {pickupName} )
              </p>
              <p className="truncate text-xs text-gray-600 font-medium">
                {pickupLocation}
              </p>
            </div>
            <div className="mt-2">
              <p className="font-bold text-[10px] text-gray-500 truncate">
                Delivery ( {deliveryName} )
              </p>
              <p className="truncate text-xs text-gray-600 font-medium">
                {deliveryLocation}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Weight + Amount */}
      <div className="flex px-4 justify-between text-sm font-medium mb-3 mt-auto">
        <div className="flex flex-col items-center justify-center">
          <span className="font-semibold text-gray-900">{weightDisplay}</span>
          <span className="text-text3 text-xs">Chargeable Weight</span>
        </div>
        <div className="flex flex-col items-center justify-center">
          {hasAmount ? (
            <>
              <span className="text-teal font-bold">₹{numAmount.toLocaleString('en-IN')}</span>
              <span className="text-text3 text-xs">Chargeable Amount</span>
            </>
          ) : (
            <>
              <span className="text-teal font-bold">
                {order.boxCount || 1} Box{Number(order.boxCount) > 1 ? 'es' : ''}
              </span>
              <span className="text-text3 text-xs">
                {order.courier || order.mode || 'SURFACE'}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Tracking + Date Footer (Teal Bar matching B2C: Tracking ID on left, DD/MM/YYYY on right) */}
      <div
        className="px-4 mt-auto bg-teal py-2 text-sm flex justify-between items-center text-white"
        onClick={(e) => {
          if (onTrackingClick) {
            e.stopPropagation();
            onTrackingClick(order);
          }
        }}
      >
        <div className="flex items-center gap-1.5 font-bold hover:underline cursor-pointer min-w-0">
          <span className="truncate">{trackingNumber}</span>
          <CopyIcon
            className="w-3.5 h-3.5 shrink-0 opacity-80 hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(trackingNumber);
              toast.success('Tracking ID copied');
            }}
          />
        </div>
        <span className="text-gray-100 text-xs shrink-0 ml-2 font-medium">
          {dateDisplay}
        </span>
      </div>
    </div>
  );
};
